import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { PinnedViewsCard } from "@/components/saved-views/pinned-views-card";
import { DataTable, type Column } from "@/components/ui/data-table";
import { StatCard } from "@/components/ui/stat-card";
import { SortScopeNote } from "@/components/ui/sort-scope-note";
import { cn, formatDate } from "@/lib/utils";
import { dateSortValue } from "@/lib/table-sort";
import { COMPLIANCE_COPY as C } from "./compliance-copy";
import { ShieldCheck, AlertTriangle, CalendarClock, Clock, Plus, Briefcase, CalendarOff, BellRing } from "lucide-react";
import { complianceNewJobHref } from "./create-job-href";

interface Trade {
    id: number;
    name: string;
}

// #1427: this is the row `GET /api/compliance/upcoming|overdue` actually
// returns (services/obligation.ts → routes/inspections.ts obligationToAssetRow).
// The old interface named fields the server never sent, so the KPIs read 0.
interface ComplianceAsset {
    id: string;
    asset_id: string;
    asset_name: string | null;
    site_id: string;
    site_name: string | null;
    customer_id: string | null;
    customer_name: string | null;
    trade_id: string | null;
    trade_name: string | null;
    due_date: string | null;
    days_until_due: number;
    days_overdue: number;
    last_inspection_date: string | null;
    authority_id: string | null;
    authority_name: string | null;
    state: string;
    blockers: Array<{ code: string; message: string }>;
}

interface MissingDueAsset {
    id: string;
    asset_name: string;
    asset_type: string | null;
    serial_number: string | null;
    next_due_date: string | null;
    last_inspection_at: string | null;
    trade_id: string | null;
    site_id: string;
    site_name: string;
    address_line: string | null;
    city: string | null;
    customer_id: string | null;
    customer_name: string | null;
}

interface DueSoonUnscheduledJob {
    id: string;
    title: string;
    status: string;
    dueByDate: string | null;
    daysUntilDue: number | null;
    customerName: string | null;
    siteCity: string | null;
    siteAddress: string | null;
    urgency: "red" | "amber";
}

const DAYS_AHEAD_OPTIONS = [
    { value: "30", label: "30 days" },
    { value: "60", label: "60 days" },
    { value: "90", label: "90 days" },
];

// Inline date editor for an asset that has no compliance due date. Saving
// PATCHes the asset and refreshes the missing-due list so the row drops off
// once a date is set.
function MissingDueDateCell({ assetId }: { assetId: string }) {
    const qc = useQueryClient();
    const [draft, setDraft] = useState("");
    const save = useMutation({
        mutationFn: (value: string) => api.patch(`/api/assets/${assetId}`, { nextDueDate: value }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["compliance", "missing-due"] });
            toast.success("Due date set");
        },
        onError: () => toast.error("Could not set due date"),
    });
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            <input
                type="date"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            />
            <Button
                size="sm"
                className="h-8 text-xs"
                disabled={!draft || save.isPending}
                onClick={() => draft && save.mutate(draft)}
            >
                Set
            </Button>
        </div>
    );
}

function AtRiskBadge() {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-[10px] font-medium">
            <AlertTriangle className="h-3 w-3" />
            At Risk
        </span>
    );
}

function CreateJobButton({ asset, navigate }: { asset: ComplianceAsset; navigate: ReturnType<typeof useNavigate> }) {
    return (
        <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={(e) => {
                e.stopPropagation();
                navigate(complianceNewJobHref(asset));
            }}
        >
            <Plus className="h-3 w-3" />
            Create Job
        </Button>
    );
}

/**
 * The screen's tab and filters, written as the obligation resolver's own
 * filter shape (services/obligation.ts). One spelling, so a pinned list and
 * the list on screen cannot drift apart.
 */
function obligationParamsFor(tab: string, tradeId: string, daysAhead: string): Record<string, unknown> | null {
    const base: Record<string, unknown> = {};
    if (tradeId) base.tradeId = tradeId;
    if (tab === "upcoming") return { ...base, leadDays: Number(daysAhead) || 30 };
    if (tab === "overdue") return { ...base, states: ["overdue"] };
    if (tab === "missing-due") return { ...base, noClock: true };
    // "Due soon, not booked" reads the dispatch surface, not the resolver.
    return null;
}

export default function CompliancePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const tab = searchParams.get("tab") ?? "upcoming";
    const tradeId = searchParams.get("tradeId") ?? "";
    const daysAhead = searchParams.get("daysAhead") ?? "30";

    const trades = useQuery({
        queryKey: ["trades"],
        queryFn: () => api.get<{ trades: Trade[] }>("/api/trades"),
    });

    const upcoming = useQuery({
        queryKey: ["compliance", "upcoming", tradeId, daysAhead],
        queryFn: () => {
            const params = new URLSearchParams({ daysAhead });
            if (tradeId) params.set("tradeId", tradeId);
            return api.get<{ assets: ComplianceAsset[]; total: number }>(`/api/compliance/upcoming?${params}`);
        },
        enabled: tab === "upcoming",
    });

    const overdue = useQuery({
        queryKey: ["compliance", "overdue", tradeId],
        queryFn: () => {
            const params = new URLSearchParams();
            if (tradeId) params.set("tradeId", tradeId);
            return api.get<{ assets: ComplianceAsset[]; total: number }>(`/api/compliance/overdue?${params}`);
        },
        enabled: tab === "overdue",
    });

    const missingDue = useQuery({
        queryKey: ["compliance", "missing-due", tradeId],
        queryFn: () => {
            const params = new URLSearchParams({ limit: "200" });
            if (tradeId) params.set("tradeId", tradeId);
            return api.get<{ assets: MissingDueAsset[]; total: number }>(`/api/compliance/missing-due?${params}`);
        },
        enabled: tab === "missing-due",
    });

    // Compliance safety net: jobs with an approaching/past due date that were
    // never placed on the schedule. Job-based (not asset-based), so it has no
    // trade filter — it queries the dispatch surface directly.
    const dueSoonUnscheduled = useQuery({
        queryKey: ["compliance", "due-soon-unscheduled"],
        queryFn: () =>
            api.get<{
                jobs: DueSoonUnscheduledJob[];
                counts: { total: number; red: number; amber: number };
                windowDays: number;
            }>("/api/dispatch/due-soon-unscheduled"),
    });

    const setTab = (t: string) =>
        setSearchParams((p) => {
            p.set("tab", t);
            return p;
        });
    const setFilter = (key: string, value: string) => {
        setSearchParams((p) => {
            if (value) p.set(key, value);
            else p.delete(key);
            return p;
        });
    };

    // #1432 — a pinned list here is an `obligations` saved view, so the same
    // filters the screen is showing are what the emailed report will carry.
    // The tabs that have no obligation-filter spelling cannot be pinned, and
    // say so rather than saving something that would mail the wrong rows.
    const pinnableParams = obligationParamsFor(tab, tradeId, daysAhead);

    function applyPinnedView(params: Record<string, unknown>) {
        setSearchParams((p) => {
            const states = Array.isArray(params.states) ? (params.states as string[]) : [];
            p.set("tab", params.noClock === true ? "missing-due" : states.includes("overdue") ? "overdue" : "upcoming");
            if (typeof params.tradeId === "string" && params.tradeId) p.set("tradeId", params.tradeId);
            else p.delete("tradeId");
            if (typeof params.leadDays === "number") p.set("daysAhead", String(params.leadDays));
            return p;
        });
    }

    const upcomingAssets = upcoming.data?.assets ?? [];
    const overdueAssets = overdue.data?.assets ?? [];
    const missingDueAssets = missingDue.data?.assets ?? [];
    const dueSoonJobs = dueSoonUnscheduled.data?.jobs ?? [];
    const totalUpcoming = upcoming.data?.total ?? 0;
    const totalOverdue = overdue.data?.total ?? 0;
    const totalMissingDue = missingDue.data?.total ?? 0;
    const totalDueSoonUnscheduled = dueSoonUnscheduled.data?.counts.total ?? 0;
    const dueThisWeek = upcomingAssets.filter((a) => (a.days_until_due ?? 999) <= 7).length;
    const avgDaysOverdue =
        overdueAssets.length > 0
            ? Math.round(overdueAssets.reduce((sum, a) => sum + (a.days_overdue ?? 0), 0) / overdueAssets.length)
            : 0;

    const upcomingColumns: Column<ComplianceAsset>[] = [
        {
            key: "asset_name",
            header: "Asset Name",
            sortable: true,
            render: (r) => <span className="font-medium">{r.asset_name}</span>,
        },
        { key: "site_name", header: "Site", sortable: true },
        { key: "customer_name", header: "Customer", sortable: true },
        {
            key: "trade_name",
            header: "Trade",
            sortable: true,
            render: (r) => (
                <Badge variant="secondary" className="text-xs">
                    {r.trade_name}
                </Badge>
            ),
        },
        {
            key: "due_date",
            header: "Due Date",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.due_date),
            render: (r) => (r.due_date ? formatDate(r.due_date) : "—"),
        },
        {
            key: "days_until_due",
            header: "Days Until Due",
            sortable: true,
            render: (r) => {
                const d = r.days_until_due ?? 0;
                return (
                    <div className="flex items-center gap-2">
                        <span className={cn("font-medium", d <= 7 && "text-amber-600", d <= 3 && "text-red-600")}>
                            {d}
                        </span>
                        {d <= 7 && <AtRiskBadge />}
                    </div>
                );
            },
        },
        {
            key: "last_inspection_date",
            header: "Last Inspection",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.last_inspection_date),
            firstSortDir: "desc",
            render: (r) =>
                r.last_inspection_date ? (
                    <div className="space-y-0.5">
                        <span className="text-sm">{formatDate(r.last_inspection_date)}</span>
                    </div>
                ) : (
                    <span className="text-foreground/80">Never</span>
                ),
        },
        {
            key: "actions",
            header: "",
            className: "w-32",
            render: (r) => <CreateJobButton asset={r} navigate={navigate} />,
        },
    ];

    const overdueColumns: Column<ComplianceAsset>[] = [
        {
            key: "asset_name",
            header: "Asset Name",
            sortable: true,
            render: (r) => <span className="font-medium">{r.asset_name}</span>,
        },
        { key: "site_name", header: "Site", sortable: true },
        { key: "customer_name", header: "Customer", sortable: true },
        {
            key: "trade_name",
            header: "Trade",
            sortable: true,
            render: (r) => (
                <Badge variant="secondary" className="text-xs">
                    {r.trade_name}
                </Badge>
            ),
        },
        {
            key: "due_date",
            header: "Due Date",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.due_date),
            render: (r) => <span className="text-red-600">{r.due_date ? formatDate(r.due_date) : "—"}</span>,
        },
        {
            key: "days_overdue",
            header: "Days Overdue",
            sortable: true,
            firstSortDir: "desc",
            render: (r) => {
                const d = r.days_overdue ?? 0;
                return (
                    <div className="flex items-center gap-2">
                        <span className={cn("font-bold", d > 30 ? "text-red-600" : "text-amber-600")}>{d}</span>
                        <AtRiskBadge />
                    </div>
                );
            },
        },
        {
            key: "last_inspection_date",
            header: "Last Inspection",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.last_inspection_date),
            firstSortDir: "desc",
            render: (r) =>
                r.last_inspection_date ? (
                    <span className="text-sm">{formatDate(r.last_inspection_date)}</span>
                ) : (
                    <span className="text-foreground/80">Never</span>
                ),
        },
        {
            key: "actions",
            header: "",
            className: "w-32",
            render: (r) => <CreateJobButton asset={r} navigate={navigate} />,
        },
    ];

    const missingDueColumns: Column<MissingDueAsset>[] = [
        {
            key: "asset_name",
            header: "Asset Name",
            sortable: true,
            render: (r) => (
                <Link to={`/assets/${r.id}`} className="font-medium hover:underline">
                    {r.asset_name}
                </Link>
            ),
        },
        { key: "customer_name", header: "Customer", sortable: true, render: (r) => r.customer_name ?? "—" },
        {
            key: "site_name",
            header: "Site",
            sortable: true,
            render: (r) => (
                <div>
                    <div>{r.site_name}</div>
                    {(r.address_line || r.city) && (
                        <div className="text-xs text-foreground/70">
                            {[r.address_line, r.city].filter(Boolean).join(", ")}
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: "serial_number",
            header: "Serial #",
            sortable: true,
            render: (r) => <span className="font-mono text-xs">{r.serial_number ?? "—"}</span>,
        },
        {
            key: "last_inspection_at",
            header: "Last Inspection",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.last_inspection_at),
            firstSortDir: "desc",
            render: (r) =>
                r.last_inspection_at ? (
                    <span className="text-sm">{formatDate(r.last_inspection_at)}</span>
                ) : (
                    <span className="text-foreground/80">Never</span>
                ),
        },
        {
            key: "set_due",
            header: "Set Due Date",
            className: "w-56",
            render: (r) => <MissingDueDateCell assetId={r.id} />,
        },
    ];

    const dueSoonColumns: Column<DueSoonUnscheduledJob>[] = [
        {
            key: "urgency",
            header: "Status",
            sortable: true,
            sortAccessor: (r) => r.daysUntilDue,
            render: (r) => {
                const days = r.daysUntilDue;
                const label =
                    days == null
                        ? "—"
                        : days < 0
                          ? `${Math.abs(days)}d overdue`
                          : days === 0
                            ? "Due today"
                            : `Due in ${days}d`;
                return (
                    <Badge
                        variant="outline"
                        className={cn(
                            "text-xs",
                            r.urgency === "red"
                                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
                        )}
                    >
                        {label}
                    </Badge>
                );
            },
        },
        {
            key: "title",
            header: "Job",
            sortable: true,
            render: (r) => (
                <Link to={`/jobs/${r.id}`} className="font-medium hover:underline">
                    {r.title}
                </Link>
            ),
        },
        { key: "customerName", header: "Customer", sortable: true, render: (r) => r.customerName ?? "—" },
        {
            key: "site",
            header: "Site",
            sortable: true,
            sortAccessor: (r) => [r.siteAddress, r.siteCity].filter(Boolean).join(", ") || null,
            render: (r) =>
                r.siteAddress || r.siteCity ? (
                    <span className="text-sm">{[r.siteAddress, r.siteCity].filter(Boolean).join(", ")}</span>
                ) : (
                    <span className="text-foreground/80">—</span>
                ),
        },
        {
            key: "dueByDate",
            header: "Due Date",
            sortable: true,
            sortAccessor: (r) => dateSortValue(r.dueByDate),
            render: (r) => (
                <span className={cn(r.urgency === "red" ? "text-red-600" : "text-amber-600")}>
                    {r.dueByDate ? formatDate(r.dueByDate) : "—"}
                </span>
            ),
        },
        {
            key: "status",
            header: "Job Status",
            sortable: true,
            render: (r) => (
                <Badge variant="secondary" className="text-xs">
                    {r.status.replace(/_/g, " ")}
                </Badge>
            ),
        },
        {
            key: "actions",
            header: "",
            className: "w-28",
            render: (r) => (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/jobs/${r.id}`);
                    }}
                >
                    <Briefcase className="h-3 w-3" />
                    Open Job
                </Button>
            ),
        },
    ];

    const tabs = [
        { key: "upcoming", label: "Upcoming", icon: CalendarClock },
        { key: "overdue", label: "Overdue", icon: AlertTriangle },
        { key: "missing-due", label: "Missing Due Date", icon: CalendarOff },
        { key: "due-soon-unscheduled", label: "Due Soon — Not Scheduled", icon: BellRing },
    ];

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-black tracking-tight text-shell-foreground flex items-center gap-2">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                    {C.section.due.title}
                </h1>
                <p className="text-shell-foreground/80 text-sm max-w-[80ch]">{C.section.due.intro}</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard title="Total Upcoming" value={totalUpcoming} icon={<CalendarClock className="h-4 w-4" />} />
                <StatCard
                    title="Total Overdue"
                    value={totalOverdue}
                    icon={<AlertTriangle className="h-4 w-4" />}
                    className={totalOverdue > 0 ? "border-l-4 border-l-red-500" : undefined}
                />
                <StatCard title="Due This Week" value={dueThisWeek} icon={<Clock className="h-4 w-4" />} />
                <StatCard
                    title="Avg Days Overdue"
                    value={avgDaysOverdue}
                    icon={<AlertTriangle className="h-4 w-4" />}
                />
            </div>

            <div className="flex gap-1 border-b">
                {tabs.map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={cn(
                            "flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors -mb-px",
                            tab === key
                                ? "border-primary text-primary font-medium"
                                : "border-transparent text-shell-foreground/80 hover:text-shell-foreground",
                        )}
                    >
                        <Icon className="h-4 w-4" />
                        {label}
                        {key === "overdue" && totalOverdue > 0 && (
                            <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 text-[10px] font-bold">
                                {totalOverdue}
                            </span>
                        )}
                        {key === "missing-due" && totalMissingDue > 0 && (
                            <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 text-[10px] font-bold">
                                {totalMissingDue}
                            </span>
                        )}
                        {key === "due-soon-unscheduled" && totalDueSoonUnscheduled > 0 && (
                            <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 text-[10px] font-bold">
                                {totalDueSoonUnscheduled}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-shell-foreground/80">Trade</label>
                    <Select className="w-40" value={tradeId} onChange={(e) => setFilter("tradeId", e.target.value)}>
                        <option value="">All Trades</option>
                        {trades.data?.trades?.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </Select>
                </div>

                {tab === "upcoming" && (
                    <div className="space-y-1">
                        <label className="text-xs font-medium text-shell-foreground/80">Window</label>
                        <Select
                            className="w-32"
                            value={daysAhead}
                            onChange={(e) => setFilter("daysAhead", e.target.value)}
                        >
                            {DAYS_AHEAD_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </div>

            <PinnedViewsCard surface="obligations" currentParams={pinnableParams} onApply={applyPinnedView} />

            {tab === "upcoming" && (
                <DataTable
                    columns={upcomingColumns}
                    data={upcomingAssets}
                    keyField="id"
                    sortId="compliance.upcoming"
                    loading={upcoming.isLoading}
                    emptyMessage="No upcoming inspections due. Devices will appear here as their annual test dates approach."
                />
            )}
            {tab === "upcoming" && (
                <SortScopeNote
                    loaded={upcomingAssets.length}
                    total={upcoming.data?.total}
                    noun="devices"
                    which="the soonest due"
                    hint=""
                />
            )}

            {tab === "overdue" && (
                <DataTable
                    columns={overdueColumns}
                    data={overdueAssets}
                    keyField="id"
                    sortId="compliance.overdue"
                    loading={overdue.isLoading}
                    emptyMessage="No overdue inspections — great job! All devices are in compliance."
                />
            )}
            {tab === "overdue" && (
                <SortScopeNote
                    loaded={overdueAssets.length}
                    total={overdue.data?.total}
                    noun="devices"
                    which="the longest overdue"
                    hint=""
                />
            )}

            {tab === "missing-due" && (
                <DataTable
                    columns={missingDueColumns}
                    data={missingDueAssets}
                    keyField="id"
                    sortId="compliance.missing-due"
                    loading={missingDue.isLoading}
                    emptyMessage="No active devices are missing a due date. Every device has a scheduled next test."
                />
            )}
            {tab === "missing-due" && (
                <SortScopeNote
                    loaded={missingDueAssets.length}
                    total={missingDue.data?.total}
                    noun="devices"
                    which="the first"
                    hint=""
                />
            )}

            {tab === "due-soon-unscheduled" && (
                <DataTable
                    columns={dueSoonColumns}
                    data={dueSoonJobs}
                    keyField="id"
                    sortId="compliance.due-soon-unscheduled"
                    loading={dueSoonUnscheduled.isLoading}
                    emptyMessage="No jobs are due soon without a schedule. Every job with an approaching compliance deadline has a time on the board."
                />
            )}
        </div>
    );
}
